import React from 'react';
import { Player } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { getAvatarHue, getInitial } from '../utils/avatar';

interface PlayerListProps {
  players: Player[];
  playersWhoSelected?: string[];
  gameState?: string;
}

const PlayerList: React.FC<PlayerListProps> = ({ players, playersWhoSelected = [], gameState }) => {
  const isSongSelectionState = gameState === 'song_selection';
  const isPlayingState = gameState === 'playing';
  const isPreloadingState = gameState === 'preloading';

  // Sort players by score when in playing state
  const sortedPlayers = isPlayingState 
    ? [...players].sort((a, b) => b.score - a.score) 
    : players;

  return (
    <div className="player-list">
      <div className="panel-heading">
        <h3>{isPlayingState ? 'Leaderboard' : 'Players'}</h3>
        <span className="count-pill">{players.length}</span>
      </div>

      <ul className="player-items">
        <AnimatePresence initial={false}>
          {sortedPlayers.map((player, index) => {
            const hasSelected = playersWhoSelected.includes(player.id);
            const isLeader = isPlayingState && index === 0 && player.score > 0;
            const isOffline = player.connected === false;

            return (
              <motion.li
                key={player.id}
                className={`player-row${isLeader ? ' is-leader' : ''}${isOffline ? ' is-offline' : ''}`}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 320, damping: 26 }}
              >
                {isPlayingState && <span className="player-rank">{index + 1}</span>}

                <span
                  className="avatar"
                  style={{ ['--hue' as string]: getAvatarHue(player.username) }}
                  aria-hidden="true"
                >
                  {getInitial(player.username)}
                </span>

                <span className="player-name">
                  <span className="player-name-text">{player.username}</span>
                  {player.isCreator && (
                    <span className="badge badge--host" title="Room host">
                      Host
                    </span>
                  )}
                  {player.isBot && (
                    <span className="badge badge--bot" title="Bot">
                      Bot
                    </span>
                  )}
                  {isOffline && (
                    <span className="badge badge--offline" title="Lost connection — their seat is kept so they can rejoin">
                      Offline
                    </span>
                  )}
                </span>

                <span className="player-meta">
                  {isPlayingState ? (
                    <motion.span
                      key={`score-${player.score}`}
                      className="player-score"
                      initial={{ scale: 1.35, color: '#34d399' }}
                      animate={{ scale: 1, color: '#f3f2fa' }}
                      transition={{ duration: 0.5 }}
                    >
                      {player.score}
                    </motion.span>
                  ) : (
                    <>
                      {player.score > 0 && <span className="player-score">{player.score}</span>}
                      {isSongSelectionState && !isPreloadingState && (
                        hasSelected ? (
                          <motion.span
                            className="status-pill status-pill--ready"
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                          >
                            Ready
                          </motion.span>
                        ) : (
                          <span className="status-pill status-pill--waiting">Picking</span>
                        )
                      )}
                    </>
                  )}
                </span>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    </div>
  );
};

export default PlayerList;
